/**
 * The reports' rules (ADR-074 in the platform repo), kept out of the components so they can be
 * tested: what a filter is and how it lives in the URL, which instants a preset like "This week"
 * names on the reader's clock, how a delta is read, how a bucket is labelled, and how a table
 * becomes a CSV (or a ZIP of them).
 *
 * The time zone is the reader's choice, not the browser's: a manager in Karachi reading a Los
 * Angeles floor wants the floor's day. Every preset is computed on that zone's clock, so "Today"
 * starts at the floor's midnight, and the server buckets on the same zone.
 */

import type { Slot } from "@/components/ui/chart";

export type ReportTab = "lines" | "campaigns" | "reps";
export const REPORT_TABS: readonly ReportTab[] = ["lines", "campaigns", "reps"];

/** The three report screens: each its own page in the sidebar, its own URL and its own filter. */
export const REPORT_SCREENS: Record<ReportTab, { path: string; title: string; description: string }> = {
  lines: {
    path: "/reports/lines",
    title: "Line report",
    description: "How callers were served: answered, lost, waited, routed.",
  },
  campaigns: {
    path: "/reports/campaigns",
    title: "Campaign report",
    description: "What the dialer placed, what came of it, and where the leads went.",
  },
  reps: {
    path: "/reports/reps",
    title: "Rep report",
    description: "How each rep spent the time, and what came of it.",
  },
};

export type RangePreset =
  | "today"
  | "yesterday"
  | "last7"
  | "last30"
  | "last90"
  | "thisWeek"
  | "lastWeek"
  | "thisMonth"
  | "lastMonth"
  | "custom";

export const RANGE_PRESETS: readonly { id: Exclude<RangePreset, "custom">; label: string }[] = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "last7", label: "Last 7 days" },
  { id: "last30", label: "Last 30 days" },
  { id: "last90", label: "Last 90 days" },
  { id: "thisWeek", label: "This week" },
  { id: "lastWeek", label: "Last week" },
  { id: "thisMonth", label: "This month" },
  { id: "lastMonth", label: "Last month" },
];

export type Granularity = "auto" | "hour" | "day" | "week" | "month";
export const GRANULARITIES: readonly { id: Granularity; label: string }[] = [
  { id: "auto", label: "Auto" },
  { id: "hour", label: "By hour" },
  { id: "day", label: "By day" },
  { id: "week", label: "By week" },
  { id: "month", label: "By month" },
];

/** off: no deltas; previous: the same length just before (or, for a period so far, the same
 *  elapsed time of the period before); week: the same hours a week earlier. */
export type CompareMode = "off" | "previous" | "week";

export type CallKind = "inbound" | "outbound" | "handoff";
export type DialMode = "power" | "preview" | "manual";

export interface ReportFilters {
  range: RangePreset;
  /** Custom range, `YYYY-MM-DD`, both days included. */
  from: string | null;
  to: string | null;
  tz: string;
  granularity: Granularity;
  compare: CompareMode;
  campaigns: string[];
  lines: string[];
  /** `user@domain` */
  reps: string[];
  /** Team ids (ADR-195 in platform): each stands for the reps in it now. */
  teams: number[];
  skills: string[];
  kinds: CallKind[];
  modes: DialMode[];
  firms: number[];
  /** ISO weekdays, 1 = Monday; empty = every day. */
  weekdays: number[];
  /** Local hours, `hourFrom` <= h < `hourTo`; null = the whole day. */
  hourFrom: number | null;
  hourTo: number | null;
  sla: number;
}

export function browserZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function defaultFilters(tz = browserZone()): ReportFilters {
  return {
    range: "last7",
    from: null,
    to: null,
    tz,
    granularity: "auto",
    compare: "previous",
    campaigns: [],
    lines: [],
    reps: [],
    teams: [],
    skills: [],
    kinds: [],
    modes: [],
    firms: [],
    weekdays: [],
    hourFrom: null,
    hourTo: null,
    sla: 20,
  };
}

/** The zones offered first: the floor's (US) ones, UTC, and whatever the browser is in. */
export function zoneChoices(current: string, browser = browserZone()): { id: string; label: string }[] {
  const known = [
    { id: "America/Los_Angeles", label: "Pacific (Los Angeles)" },
    { id: "America/Denver", label: "Mountain (Denver)" },
    { id: "America/Phoenix", label: "Arizona (Phoenix)" },
    { id: "America/Chicago", label: "Central (Chicago)" },
    { id: "America/New_York", label: "Eastern (New York)" },
    { id: "UTC", label: "UTC" },
  ];
  const out = [...known];
  for (const zone of [browser, current]) {
    if (zone && !out.some((z) => z.id === zone)) out.push({ id: zone, label: `${zone.replaceAll("_", " ")}` });
  }
  return out;
}

// --- the filter in the URL ---------------------------------------------------------------------
//
// Short keys, and only what differs from the defaults: a link to a report is something people
// paste into Slack, and it should survive being read.

const KINDS: readonly CallKind[] = ["inbound", "outbound", "handoff"];
const MODES: readonly DialMode[] = ["power", "preview", "manual"];
const PRESET_IDS = new Set<string>([...RANGE_PRESETS.map((p) => p.id), "custom"]);
const GRANULARITY_IDS = new Set<string>(GRANULARITIES.map((g) => g.id));

const DAY = /^\d{4}-\d{2}-\d{2}$/;

export function filtersFromSearch(params: URLSearchParams, base = defaultFilters()): ReportFilters {
  const list = (key: string) => params.getAll(key).flatMap((v) => v.split(",")).map((v) => v.trim()).filter(Boolean);
  const range = params.get("range") ?? base.range;
  const from = params.get("from");
  const to = params.get("to");
  const hours = /^(\d{1,2})-(\d{1,2})$/.exec(params.get("h") ?? "");
  const hourFrom = hours ? Number(hours[1]) : null;
  const hourTo = hours ? Number(hours[2]) : null;
  const validHours =
    hourFrom !== null && hourTo !== null && hourFrom >= 0 && hourFrom <= 23 && hourTo >= 1 && hourTo <= 24 &&
    hourFrom !== hourTo;
  const cmp = params.get("cmp");
  const sla = Number(params.get("sla"));
  const custom = range === "custom" && from && to && DAY.test(from) && DAY.test(to) && from <= to;
  return {
    range: custom ? "custom" : PRESET_IDS.has(range) && range !== "custom" ? (range as RangePreset) : base.range,
    from: custom ? from : null,
    to: custom ? to : null,
    tz: params.get("tz") || base.tz,
    granularity: GRANULARITY_IDS.has(params.get("g") ?? "") ? (params.get("g") as Granularity) : base.granularity,
    compare: cmp === "off" || cmp === "previous" || cmp === "week" ? cmp : base.compare,
    campaigns: list("c"),
    lines: list("l"),
    reps: list("r").filter((r) => r.includes("@")),
    teams: list("t").map(Number).filter((n) => Number.isInteger(n) && n > 0),
    skills: list("s"),
    kinds: list("k").filter((k): k is CallKind => (KINDS as readonly string[]).includes(k)),
    modes: list("m").filter((m): m is DialMode => (MODES as readonly string[]).includes(m)),
    firms: list("f").map(Number).filter((n) => Number.isInteger(n) && n > 0),
    weekdays: [...new Set((params.get("wd") ?? "").split("").map(Number))].filter((d) => d >= 1 && d <= 7).sort(),
    hourFrom: validHours ? hourFrom : null,
    hourTo: validHours ? hourTo : null,
    sla: Number.isInteger(sla) && sla >= 1 && sla <= 600 ? sla : base.sla,
  };
}

export function searchFromFilters(f: ReportFilters, base = defaultFilters(f.tz)): URLSearchParams {
  const out = new URLSearchParams();
  if (f.range !== base.range || f.range === "custom") out.set("range", f.range);
  if (f.range === "custom" && f.from && f.to) {
    out.set("from", f.from);
    out.set("to", f.to);
  }
  if (f.tz !== browserZone()) out.set("tz", f.tz);
  if (f.granularity !== base.granularity) out.set("g", f.granularity);
  if (f.compare !== base.compare) out.set("cmp", f.compare);
  if (f.campaigns.length) out.set("c", f.campaigns.join(","));
  if (f.lines.length) out.set("l", f.lines.join(","));
  if (f.reps.length) out.set("r", f.reps.join(","));
  if (f.teams.length) out.set("t", f.teams.join(","));
  if (f.skills.length) out.set("s", f.skills.join(","));
  if (f.kinds.length) out.set("k", f.kinds.join(","));
  if (f.modes.length) out.set("m", f.modes.join(","));
  if (f.firms.length) out.set("f", f.firms.join(","));
  if (f.weekdays.length && f.weekdays.length < 7) out.set("wd", [...f.weekdays].sort().join(""));
  if (f.hourFrom !== null && f.hourTo !== null) out.set("h", `${f.hourFrom}-${f.hourTo}`);
  if (f.sla !== base.sla) out.set("sla", String(f.sla));
  return out;
}

/** How many filters narrow the report beyond its window: the badge on the Filters button. */
/**
 * The part of a filter that means the same on all three screens: the window, whose clock, the
 * comparison, the grouping and the hours. It follows a person from one report to the next (kept
 * in this browser), so opening the rep report after the line report shows the same days on the
 * same clock. What to narrow to stays with its own screen.
 */
export type ReportPeriod = Pick<
  ReportFilters,
  "range" | "from" | "to" | "tz" | "granularity" | "compare" | "weekdays" | "hourFrom" | "hourTo"
>;

export const PERIOD_STORAGE_KEY = "acl.reports.period";

export function periodOf(f: ReportFilters): ReportPeriod {
  const { range, from, to, tz, granularity, compare, weekdays, hourFrom, hourTo } = f;
  return { range, from, to, tz, granularity, compare, weekdays, hourFrom, hourTo };
}

/** A stored period, read back through the URL rules so nothing malformed survives the trip. */
export function withPeriod(f: ReportFilters, stored: unknown): ReportFilters {
  if (!stored || typeof stored !== "object") return f;
  const period = stored as Partial<ReportPeriod>;
  const merged = { ...f, ...period } as ReportFilters;
  return { ...f, ...periodOf(filtersFromSearch(searchFromFilters(merged), f)) };
}

/** The filter with nothing narrowing it: every campaign, line, rep and hour, the same window. */
export function withoutNarrowing(f: ReportFilters): ReportFilters {
  return {
    ...f,
    campaigns: [],
    lines: [],
    reps: [],
    teams: [],
    skills: [],
    kinds: [],
    modes: [],
    firms: [],
    weekdays: [],
    hourFrom: null,
    hourTo: null,
  };
}

export function narrowingCount(f: ReportFilters): number {
  return (
    f.campaigns.length + f.lines.length + f.reps.length + f.teams.length + f.skills.length + f.kinds.length +
    f.modes.length + f.firms.length + (f.weekdays.length ? 1 : 0) + (f.hourFrom !== null ? 1 : 0)
  );
}

// --- wall-clock time in a zone -----------------------------------------------------------------

interface Ymd {
  y: number;
  m: number;
  d: number;
}

const partsCache = new Map<string, Intl.DateTimeFormat>();

function formatterFor(tz: string): Intl.DateTimeFormat {
  let f = partsCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    partsCache.set(tz, f);
  }
  return f;
}

/** The wall clock in `tz` at an instant. */
export function wallClock(instant: number, tz: string): Ymd & { hh: number; mm: number; ss: number } {
  const parts: Record<string, number> = {};
  for (const p of formatterFor(tz).formatToParts(new Date(instant))) {
    if (p.type !== "literal") parts[p.type] = Number(p.value);
  }
  return { y: parts.year!, m: parts.month!, d: parts.day!, hh: parts.hour! % 24, mm: parts.minute!, ss: parts.second! };
}

/** Minutes `tz` is ahead of UTC at an instant. */
export function zoneOffset(instant: number, tz: string): number {
  const w = wallClock(instant, tz);
  const asUtc = Date.UTC(w.y, w.m - 1, w.d, w.hh, w.mm, w.ss);
  return Math.round((asUtc - Math.floor(instant / 1000) * 1000) / 60_000);
}

/** The instant a wall-clock time in `tz` names. A time the clocks skip lands just after the gap. */
export function zonedInstant(day: Ymd, tz: string, hh = 0, mm = 0): number {
  const guess = Date.UTC(day.y, day.m - 1, day.d, hh, mm);
  const first = guess - zoneOffset(guess, tz) * 60_000;
  const second = guess - zoneOffset(first, tz) * 60_000;
  return second;
}

function addDays(day: Ymd, n: number): Ymd {
  const t = new Date(Date.UTC(day.y, day.m - 1, day.d + n));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}

function isoWeekday(day: Ymd): number {
  const w = new Date(Date.UTC(day.y, day.m - 1, day.d)).getUTCDay();
  return w === 0 ? 7 : w;
}

function parseDay(value: string): Ymd {
  const [y, m, d] = value.split("-").map(Number);
  return { y: y!, m: m!, d: d! };
}

export function dayString(day: Ymd): string {
  return `${day.y}-${String(day.m).padStart(2, "0")}-${String(day.d).padStart(2, "0")}`;
}

/** Today on the reader's clock, `YYYY-MM-DD`. */
export function todayIn(tz: string, now = Date.now()): string {
  return dayString(wallClock(now, tz));
}

export interface ReportWindowInstants {
  since: string;
  until: string;
  /** Set when the comparison is not simply the same length just before. */
  compareSince?: string;
  compareUntil?: string;
}

/**
 * The instants a filter names. `now` is rounded down to the minute by the caller so the query key
 * holds still between renders. A period "so far" (today, this week, this month) is compared with
 * the same elapsed time of the period before it — today until 10:30 against yesterday until 10:30,
 * not against the hours before midnight.
 */
export function windowOf(f: ReportFilters, now: number): ReportWindowInstants {
  const today = wallClock(now, f.tz);
  const at = (day: Ymd) => zonedInstant(day, f.tz);
  let since: number;
  let until: number;
  let prevSince: number | undefined;
  let prevUntil: number | undefined;
  switch (f.range) {
    case "today":
      since = at(today);
      until = now;
      prevSince = at(addDays(today, -1));
      prevUntil = prevSince + (now - since);
      break;
    case "yesterday":
      since = at(addDays(today, -1));
      until = at(today);
      break;
    case "last30":
      since = at(addDays(today, -29));
      until = now;
      break;
    case "last90":
      since = at(addDays(today, -89));
      until = now;
      break;
    case "thisWeek": {
      const monday = addDays(today, 1 - isoWeekday(today));
      since = at(monday);
      until = now;
      prevSince = at(addDays(monday, -7));
      prevUntil = prevSince + (now - since);
      break;
    }
    case "lastWeek": {
      const monday = addDays(today, 1 - isoWeekday(today));
      since = at(addDays(monday, -7));
      until = at(monday);
      break;
    }
    case "thisMonth": {
      const first = { y: today.y, m: today.m, d: 1 };
      const before = today.m === 1 ? { y: today.y - 1, m: 12, d: 1 } : { y: today.y, m: today.m - 1, d: 1 };
      since = at(first);
      until = now;
      prevSince = at(before);
      prevUntil = Math.min(prevSince + (now - since), since);
      break;
    }
    case "lastMonth": {
      const first = { y: today.y, m: today.m, d: 1 };
      const before = today.m === 1 ? { y: today.y - 1, m: 12, d: 1 } : { y: today.y, m: today.m - 1, d: 1 };
      since = at(before);
      until = at(first);
      break;
    }
    case "custom": {
      const from = f.from ? parseDay(f.from) : addDays(today, -6);
      const to = f.to ? parseDay(f.to) : today;
      since = at(from);
      until = at(addDays(to, 1));
      break;
    }
    case "last7":
    default:
      since = at(addDays(today, -6));
      until = now;
  }
  if (f.compare === "week") {
    prevSince = since - 7 * 86_400_000;
    prevUntil = until - 7 * 86_400_000;
  }
  const iso = (t: number) => new Date(t).toISOString();
  return {
    since: iso(since),
    until: iso(until),
    ...(f.compare !== "off" && prevSince !== undefined && prevUntil !== undefined
      ? { compareSince: iso(prevSince), compareUntil: iso(prevUntil) }
      : {}),
  };
}

/** The label a window reads as on the page: "Last 7 days", or "Sep 1 – Sep 14" for a custom one. */
export function rangeLabel(f: ReportFilters): string {
  if (f.range === "custom" && f.from && f.to) {
    const a = parseDay(f.from);
    const b = parseDay(f.to);
    const fmt = (d: Ymd) => `${MONTHS[d.m - 1]!.slice(0, 3)} ${d.d}${d.y !== b.y || a.y !== b.y ? `, ${d.y}` : ""}`;
    return f.from === f.to ? fmt(a) : `${fmt(a)} – ${fmt(b)}`;
  }
  return RANGE_PRESETS.find((p) => p.id === f.range)?.label ?? "Last 7 days";
}

/** What the comparison is called beside a delta. */
export function compareLabel(f: ReportFilters): string {
  if (f.compare === "week") return "vs the same time last week";
  switch (f.range) {
    case "today":
      return "vs yesterday at this time";
    case "yesterday":
      return "vs the day before";
    case "thisWeek":
      return "vs last week at this point";
    case "lastWeek":
      return "vs the week before";
    case "thisMonth":
      return "vs last month at this point";
    case "lastMonth":
      return "vs the month before";
    default:
      return "vs the period before";
  }
}

/** The query every report route takes, with lists as repeated keys. */
export function reportQuery(f: ReportFilters, w: ReportWindowInstants): string {
  const out = new URLSearchParams();
  out.set("since", w.since);
  out.set("until", w.until);
  out.set("tz", f.tz);
  out.set("granularity", f.granularity);
  if (f.compare !== "off") {
    out.set("compare", "true");
    if (w.compareSince && w.compareUntil) {
      out.set("compare_since", w.compareSince);
      out.set("compare_until", w.compareUntil);
    }
  }
  for (const c of f.campaigns) out.append("campaign_id", c);
  for (const l of f.lines) out.append("queue", l);
  for (const r of f.reps) out.append("rep", r);
  for (const t of f.teams) out.append("team", String(t));
  for (const s of f.skills) out.append("skill", s);
  for (const k of f.kinds) out.append("kind", k);
  for (const m of f.modes) out.append("mode", m);
  for (const firm of f.firms) out.append("firm_id", String(firm));
  for (const d of f.weekdays) out.append("weekday", String(d));
  if (f.hourFrom !== null && f.hourTo !== null) {
    out.set("hour_from", String(f.hourFrom));
    out.set("hour_to", String(f.hourTo));
  }
  out.set("sla_seconds", String(f.sla));
  return `?${out.toString()}`;
}

// --- buckets -----------------------------------------------------------------------------------

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;
export const WEEKDAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] as const;

/**
 * A bucket's start as the server labels it — its wall clock in the report's zone, offset
 * included (`2026-09-21T00:00:00-07:00`). Read the wall clock straight off the string: turning it
 * into a `Date` would move it into the BROWSER's zone.
 */
export function bucketClock(start: string): (Ymd & { hh: number; mm: number }) | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(start);
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]), hh: Number(m[4]), mm: Number(m[5]) };
}

export function hourName(hh: number): string {
  if (hh === 0) return "12a";
  if (hh === 12) return "12p";
  return hh < 12 ? `${hh}a` : `${hh - 12}p`;
}

/** The axis's short label for a bucket. */
export function bucketLabel(start: string, granularity: string): string {
  const c = bucketClock(start);
  if (!c) return start;
  const month = MONTHS[c.m - 1]!.slice(0, 3);
  switch (granularity) {
    case "hour":
      return c.hh === 0 ? `${month} ${c.d}` : hourName(c.hh);
    case "month":
      return c.m === 1 ? `${month} ${String(c.y).slice(2)}` : month;
    default:
      return `${month} ${c.d}`;
  }
}

/** The tooltip's and the export's full label for a bucket. */
export function bucketTitle(start: string, granularity: string): string {
  const c = bucketClock(start);
  if (!c) return start;
  const weekday = WEEKDAY_NAMES[isoWeekday(c) - 1]!.slice(0, 3);
  const month = MONTHS[c.m - 1]!;
  switch (granularity) {
    case "hour":
      return `${weekday} ${month.slice(0, 3)} ${c.d}, ${hourName(c.hh)}–${hourName((c.hh + 1) % 24)}`;
    case "week":
      return `Week of ${month.slice(0, 3)} ${c.d}, ${c.y}`;
    case "month":
      return `${month} ${c.y}`;
    default:
      return `${weekday} ${month.slice(0, 3)} ${c.d}, ${c.y}`;
  }
}

// --- numbers -----------------------------------------------------------------------------------

export function fmtCount(n: number | null | undefined): string {
  if (n == null) return "—";
  return Math.round(n).toLocaleString("en-US");
}

/** A headline number: exact below ten thousand, compact above it (12.4K, 3.1M). */
export function fmtCompact(n: number | null | undefined): string {
  if (n == null) return "—";
  if (Math.abs(n) < 10_000) return fmtCount(n);
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(n);
}

/** A share: one decimal under 10%, whole numbers above; null is "—", never 0%. */
export function fmtPct(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const p = v * 100;
  if (p !== 0 && Math.abs(p) < 10) return `${p.toFixed(1)}%`;
  return `${Math.round(p)}%`;
}

/** A duration in the unit a person reads it in: 4.2s, 3m 05s, 2h 07m. */
export function fmtSeconds(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const s = Math.max(0, v);
  if (s < 10) return `${s.toFixed(1)}s`;
  if (s < 60) return `${Math.round(s)}s`;
  const total = Math.round(s);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${m}m ${String(sec).padStart(2, "0")}s`;
}

/** Hours with one decimal, for time signed in and time in state: "37.5 h". */
export function fmtHours(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  return `${(seconds / 3600).toFixed(seconds >= 36_000 ? 0 : 1)} h`;
}

/** A clock time on the reader's zone, for a timesheet: "8:02 AM". */
export function fmtClock(iso: string | null | undefined, tz: string): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" }).format(new Date(iso));
}

/** A span as an axis says it: 45s, 2m, 4h, 3d. */
export function shortSpan(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86_400)}d`;
}

/** A histogram bin: "Under 10s", "10s–20s", "5m+". */
export function binLabel(lo: number, hi: number | null): string {
  if (hi == null) return `${shortSpan(lo)}+`;
  if (lo === 0) return `Under ${shortSpan(hi)}`;
  return `${shortSpan(lo)}–${shortSpan(hi)}`;
}

export type Tone = "good" | "bad" | "flat" | "none";

export interface Delta {
  label: string;
  tone: Tone;
}

/**
 * A change against the comparison window, read the way the number is: a rate moves in points
 * ("+3.1 pts"), anything else by how much of itself (+12%). `better` says which way is good — a
 * higher answer rate is, a longer wait is not — and `neutral` colours neither.
 */
export function delta(
  current: number | null | undefined,
  previous: number | null | undefined,
  kind: "count" | "rate" | "seconds",
  better: "up" | "down" | "neutral",
): Delta | null {
  if (current == null || previous == null || !Number.isFinite(current) || !Number.isFinite(previous)) return null;
  const tone = (diff: number, tiny: number): Tone => {
    if (Math.abs(diff) < tiny) return "flat";
    if (better === "neutral") return "flat";
    return (diff > 0) === (better === "up") ? "good" : "bad";
  };
  if (kind === "rate") {
    const diff = (current - previous) * 100;
    if (Math.abs(diff) < 0.05) return { label: "no change", tone: "flat" };
    return { label: `${diff > 0 ? "+" : "−"}${Math.abs(diff).toFixed(1)} pts`, tone: tone(diff, 0.05) };
  }
  if (previous === 0) {
    return current === 0 ? { label: "no change", tone: "flat" } : { label: "new", tone: tone(current, 0) };
  }
  const rel = (current - previous) / Math.abs(previous);
  if (Math.abs(rel) < 0.005) return { label: "no change", tone: "flat" };
  const pct = Math.abs(rel) >= 10 ? `${Math.round(Math.abs(rel))}×` : `${Math.round(Math.abs(rel) * 100)}%`;
  return { label: `${rel > 0 ? "+" : "−"}${pct}`, tone: tone(rel, 0.005) };
}

// --- vocabulary --------------------------------------------------------------------------------

export const KIND_LABELS: Record<string, string> = {
  inbound: "Inbound",
  outbound: "Outbound",
  handoff: "Handoff",
  unknown: "Unknown",
};
export const MODE_LABELS: Record<string, string> = { power: "Power", preview: "Preview", manual: "Manual", unknown: "Unknown" };
export const STARTER_LABELS: Record<string, string> = {
  cadence: "Cadence",
  system: "Dialer",
  rep: "Rep",
  unknown: "Unknown",
};
export const DID_STRATEGY_LABELS: Record<string, string> = {
  branded: "Campaign number",
  same_as_last: "Same as last call",
  local: "Local number",
  unbranded: "Local number",
  not_recorded: "Not recorded",
};
export const BLOCK_REASON_LABELS: Record<string, string> = {
  dnc: "Do not call",
  no_consent: "No consent",
  no_timezone: "No time zone",
  quiet_hours: "Outside calling hours",
  unknown: "Unknown",
};
export const PROBLEM_LABELS: Record<string, string> = {
  no_trunk: "No trunk",
  no_caller_id: "No caller ID",
  no_route: "No route",
};
export const MISSED_LABELS: Record<string, string> = {
  ringout: "Rang out",
  rejected: "Declined",
  failed: "Could not ring",
};
export const HANDOFF_MODE_LABELS: Record<string, string> = { blind: "Blind", consult: "Consult", unknown: "Not recorded" };

export function labelOf(map: Record<string, string>, key: string | null | undefined): string {
  if (!key) return "—";
  return map[key] ?? key.replaceAll("_", " ").replace(/^./, (c) => c.toUpperCase());
}

/** Rep states, as the stacked time bar groups them: the colour is the group's, in a fixed order. */
export const STATE_GROUPS: readonly { key: string; label: string; slot: Slot; states: readonly string[] }[] = [
  { key: "talk", label: "On calls", slot: 1, states: ["busy", "busy_external"] },
  { key: "after", label: "After-call work", slot: 2, states: ["disposition", "wrapup"] },
  { key: "reach", label: "Ringing and dialing", slot: 3, states: ["ringing", "dialing"] },
  { key: "available", label: "Available", slot: 4, states: ["available"] },
  { key: "paused", label: "On break", slot: 5, states: ["paused"] },
];

export function stateGroups(seconds: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const g of STATE_GROUPS) out[g.key] = g.states.reduce((acc, s) => acc + (seconds[s] ?? 0), 0);
  return out;
}

export function breakLabel(code: string): string {
  return code.replaceAll("_", " ").replace(/^./, (c) => c.toUpperCase());
}

// --- export ------------------------------------------------------------------------------------

export interface CsvColumn<T> {
  header: string;
  value: (row: T) => string | number | boolean | null | undefined;
}

/**
 * A table as CSV, RFC 4180, with a byte-order mark so Excel reads the accents and the dashes.
 * A text cell that starts like a formula (= + - @) is prefixed with an apostrophe: rep and
 * campaign names are typed by people, and a spreadsheet would run them.
 */
export function toCsv<T>(columns: readonly CsvColumn<T>[], rows: readonly T[]): string {
  const cell = (value: unknown): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === "number") return Number.isFinite(value) ? String(value) : "";
    if (typeof value === "boolean") return value ? "yes" : "no";
    let text = String(value);
    if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
    return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
  };
  const lines = [columns.map((c) => cell(c.header)).join(",")];
  for (const row of rows) lines.push(columns.map((c) => cell(c.value(row))).join(","));
  return `﻿${lines.join("\r\n")}\r\n`;
}

/** A file name for an export: the report, the section and the window, in plain characters. */
export function exportName(parts: readonly string[], extension: string): string {
  const slug = parts
    .join("-")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${slug || "report"}.${extension}`;
}

let crcTable: Uint32Array | null = null;

function crc32(bytes: Uint8Array): number {
  if (!crcTable) {
    crcTable = new Uint32Array(256);
    for (let n = 0; n < 256; n += 1) {
      let c = n;
      for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      crcTable[n] = c >>> 0;
    }
  }
  let crc = 0xffffffff;
  for (const b of bytes) crc = crcTable[(crc ^ b) & 0xff]! ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

/**
 * Several files as one ZIP, stored rather than compressed: CSVs are small, and a stored archive
 * is a page of code where a compressing one is a dependency. Every spreadsheet tool opens it.
 */
export function zipFiles(files: readonly { name: string; content: string }[]): Uint8Array<ArrayBuffer> {
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;
  for (const file of files) {
    const name = encoder.encode(file.name);
    const data = encoder.encode(file.content);
    const crc = crc32(data);
    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true);
    local.setUint16(6, 0x0800, true); // UTF-8 names
    local.setUint16(8, 0, true); // stored
    local.setUint16(10, 0, true);
    local.setUint16(12, 0x21, true); // 1980-01-01
    local.setUint32(14, crc, true);
    local.setUint32(18, data.length, true);
    local.setUint32(22, data.length, true);
    local.setUint16(26, name.length, true);
    local.setUint16(28, 0, true);
    chunks.push(new Uint8Array(local.buffer), name, data);
    const entry = new DataView(new ArrayBuffer(46));
    entry.setUint32(0, 0x02014b50, true);
    entry.setUint16(4, 20, true);
    entry.setUint16(6, 20, true);
    entry.setUint16(8, 0x0800, true);
    entry.setUint16(10, 0, true);
    entry.setUint16(12, 0, true);
    entry.setUint16(14, 0x21, true);
    entry.setUint32(16, crc, true);
    entry.setUint32(20, data.length, true);
    entry.setUint32(24, data.length, true);
    entry.setUint16(28, name.length, true);
    entry.setUint32(42, offset, true);
    central.push(new Uint8Array(entry.buffer), name);
    offset += 30 + name.length + data.length;
  }
  const centralSize = central.reduce((acc, c) => acc + c.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(8, files.length, true);
  end.setUint16(10, files.length, true);
  end.setUint32(12, centralSize, true);
  end.setUint32(16, offset, true);
  const all = [...chunks, ...central, new Uint8Array(end.buffer)];
  const out = new Uint8Array(all.reduce((acc, c) => acc + c.length, 0));
  let at = 0;
  for (const c of all) {
    out.set(c, at);
    at += c.length;
  }
  return out;
}

// --- saved views -------------------------------------------------------------------------------

/** Where the saved views live in `ui_prefs` (one key, replaced whole on save). */
export const VIEWS_PREF = "report_views";
export const MAX_VIEWS = 30;

export interface SavedView {
  id: string;
  name: string;
  tab: ReportTab;
  /** The filter as the URL carries it, without the leading `?`. */
  search: string;
  /** Opened when its screen is opened with no filter of its own. One per screen. */
  pinned?: boolean;
}

export function viewsFromPrefs(prefs: unknown): SavedView[] {
  const raw = (prefs as Record<string, unknown> | null | undefined)?.[VIEWS_PREF];
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (v): v is SavedView =>
      !!v && typeof v === "object" && typeof (v as SavedView).id === "string" &&
      typeof (v as SavedView).name === "string" && typeof (v as SavedView).search === "string" &&
      (REPORT_TABS as readonly string[]).includes((v as SavedView).tab),
  );
}

/** Save under a name: the same name replaces the view it names, so saving twice is not two. */
export function saveView(
  views: readonly SavedView[],
  view: Omit<SavedView, "id">,
  id: string = crypto.randomUUID(),
): SavedView[] {
  const name = view.name.trim();
  const existing = views.find((v) => v.name.toLowerCase() === name.toLowerCase());
  const next: SavedView = { ...view, name, id: existing?.id ?? id };
  const rest = views.filter((v) => v.id !== next.id);
  const pinnedCleared = next.pinned ? rest.map((v) => (v.tab === next.tab ? { ...v, pinned: false } : v)) : rest;
  return [next, ...pinnedCleared].slice(0, MAX_VIEWS);
}

/** Make one view the screen's default (null: none). The other screens' defaults stay as they are. */
export function pinView(views: readonly SavedView[], id: string | null, tab: ReportTab): SavedView[] {
  return views.map((v) => (v.tab === tab ? { ...v, pinned: v.id === id } : v));
}
